import { normalizeExportPath } from '@couplespace/core/notion-export';

import { FILE_BLOCK_ID_PROP } from '@/components/editor/file-block-persist';
import { presignUpload } from '@/lib/blocks/api';

import type { ImportContentBlockPayload } from './markdown-to-content.js';

export type AssetFileSource = {
  files: Map<string, File>;
};

export type AssetResolveResult = {
  blocks: ImportContentBlockPayload[];
  uploadFailures: string[];
};

function resolveAssetFile(files: Map<string, File>, url: string, mdPath: string): File | null {
  const norm = normalizeExportPath(decodeURIComponent(url));
  const direct = files.get(norm);
  if (direct) {
    return direct;
  }
  const baseDir = mdPath.includes('/') ? mdPath.replace(/\/[^/]+$/, '') : '';
  const joined = baseDir ? `${baseDir}/${norm}` : norm;
  const fromBase = files.get(normalizeExportPath(joined));
  if (fromBase) {
    return fromBase;
  }
  for (const [key, file] of files) {
    if (key.endsWith(norm) || key.endsWith(`/${norm}`)) {
      return file;
    }
  }
  return null;
}

export async function resolveImagesInContentBlocks(
  scan: AssetFileSource,
  mdPath: string,
  uploadBlockId: string,
  blocks: ImportContentBlockPayload[],
): Promise<AssetResolveResult> {
  const out: ImportContentBlockPayload[] = [];
  const uploadFailures: string[] = [];

  for (const block of blocks) {
    if (block.type !== 'image' && block.type !== 'file') {
      out.push(block);
      continue;
    }
    const props = (block.content.props as Record<string, unknown> | undefined) ?? {};
    const url = typeof props.url === 'string' ? props.url : null;
    if (
      !url ||
      /^https?:\/\//i.test(url) ||
      url.startsWith('data:') ||
      url.startsWith('/page/') ||
      url.startsWith('/database/')
    ) {
      out.push(block);
      continue;
    }
    const file = resolveAssetFile(scan.files, url, mdPath);
    if (!file) {
      console.warn('[CS-NOTION] asset:missing', { url, mdPath });
      uploadFailures.push(`Missing file: ${url}`);
      out.push(block);
      continue;
    }
    try {
      console.log('[CS-NOTION] upload:start', {
        blockId: uploadBlockId,
        name: file.name,
        type: file.type,
        size: file.size,
        url,
      });
      const { fileId, uploadUrl } = await presignUpload({
        blockId: uploadBlockId,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      console.log('[CS-NOTION] presign:ok', { name: file.name, fileId });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      console.log('[CS-NOTION] put:done', {
        name: file.name,
        status: putRes.status,
        ok: putRes.ok,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }
      out.push({
        ...block,
        content: {
          ...block.content,
          props: { ...props, [FILE_BLOCK_ID_PROP]: fileId, url: undefined },
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'upload failed';
      console.error('[CS-NOTION] upload:error', { name: file.name, message });
      uploadFailures.push(`${file.name}: ${message}`);
      out.push(block);
    }
  }
  return { blocks: out, uploadFailures };
}
